import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, ArrowUpRight } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import FeaturedVideoHero from "../components/FeaturedVideoHero";

export default function VideosPage() {
  const [items, setItems] = useState([]);
  const [banner, setBanner] = useState(null);
  const gridRef = useRef(null);

  useEffect(() => {
    api.get("/content/videos").then((r) => setItems(r.data));
    api.get("/banners?placement=home_mid").then((r) => setBanner(r.data?.[0] || null));
  }, []);

  const featured = items.find((v) => v.featured) || items[0];
  const rest = items.filter((v) => v.id !== featured?.id);

  const scrollToGrid = () =>
    gridRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="pb-32" data-testid="videos-page">
      <FeaturedVideoHero video={featured} onBrowse={scrollToGrid} />

      {/* VIDEO GRID */}
      <section ref={gridRef} className="border-b border-neutral-800" data-testid="videos-grid-section">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14">
          <div className="flex items-end justify-between mb-8 gap-4 flex-wrap">
            <div>
              <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">THE VISUALS</div>
              <h2 className="font-headline text-2xl md:text-4xl uppercase">More Music Videos</h2>
            </div>
            <span className="font-mono-label text-[10px] text-neutral-500">
              {items.length} TOTAL · {items.filter((v) => v.is_official).length} OFFICIAL
            </span>
          </div>

          {rest.length === 0 ? (
            <div className="border border-dashed border-neutral-800 p-12 text-center font-mono-label text-xs text-neutral-500">
              More videos dropping soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="videos-grid">
              {rest.map((v) => (
                <VideoTile key={v.id} v={v} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SPONSORED BANNER */}
      {banner && (
        <section className="border-b border-neutral-800" data-testid="sponsored-banner-section">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-10">
            <a
              href={banner.link || "#"}
              target={banner.link?.startsWith("http") ? "_blank" : undefined}
              rel="noreferrer"
              className="relative block overflow-hidden border border-neutral-800 group"
              data-testid="sponsored-banner"
            >
              <div className="aspect-[16/5] relative">
                <img
                  src={resolveFileUrl(banner.image)}
                  alt={banner.title}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent flex items-center">
                  <div className="px-6 sm:px-10 max-w-2xl">
                    <div className="font-mono-label text-[10px] text-[var(--accent)] mb-2">SPONSORED</div>
                    <div className="font-display text-3xl sm:text-5xl uppercase leading-none">{banner.title}</div>
                    {banner.subtitle && (
                      <div className="font-headline text-base sm:text-lg text-neutral-300 uppercase mt-2">{banner.subtitle}</div>
                    )}
                  </div>
                  <div className="ml-auto pr-6 sm:pr-10">
                    <span className="hidden sm:inline-flex items-center gap-2 bg-white text-black font-mono-label text-[11px] px-5 py-3">
                      LEARN MORE <ArrowUpRight size={14} />
                    </span>
                  </div>
                </div>
              </div>
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function VideoTile({ v }) {
  return (
    <Link
      to={`/videos/${v.id}`}
      className="card-brut group block"
      data-testid={`video-card-${v.id}`}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={resolveFileUrl(v.cover_image)}
          alt={v.title}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 bg-[var(--accent)] text-black font-mono-label text-[10px] px-2 py-1" data-testid={`video-chip-${v.id}`}>
          {(v.genre || "MUSIC").toUpperCase()}
        </span>
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 bg-[var(--accent)] flex items-center justify-center">
            <Play size={20} />
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-neutral-800">
        <div className="font-headline text-base uppercase truncate">{v.title}</div>
        <div className="font-mono-label text-[10px] text-neutral-400 mt-1 flex justify-between">
          <span className="truncate" data-testid={`video-artist-${v.id}`}>{v.artist}</span>
          <span>{v.duration}</span>
        </div>
        <span className="mt-3 flex items-center justify-center gap-2 py-2 bg-[var(--accent)] group-hover:bg-[var(--accent-hover)] text-white font-mono-label text-[10px] uppercase transition-colors" data-testid={`watch-now-${v.id}`}>
          <Play size={11} fill="currentColor" /> Watch Now
        </span>
      </div>
    </Link>
  );
}
